import type { XrefPumps } from "@/composables/forms/formTypes";
import { getNominalPressureHeadFt } from "@/libs/calculations/pumps";
import { DEFAULT_ESP_PUMP_RPM } from "@/libs/calculations/constants";
import { GallonsPerMinute } from "@/libs/units/flowRate";
import type { IndividualPumpRecommendedViscosityCorrections } from "./types";

// calcs from Pump Viscosity Correction Calcs 2.xlsx
export function getPumpRecCorrections(
  xrefPump: XrefPumps[number],
  liquidRateSTBD: number,
  averageViscosityCP: number,
  operatingRPM: number,
  averageLiquidSpecificGravity: number
): IndividualPumpRecommendedViscosityCorrections {
  const correctionData = {
    recRateCorrection: 1,
    recHeadCorrection: 1,
    recBHPCorrection: 1
  };
  if (
    !xrefPump.r_pump ||
    !liquidRateSTBD ||
    !averageViscosityCP ||
    !averageLiquidSpecificGravity
  ) {
    return correctionData;
  }
  const pump = xrefPump.r_pump;
  const qBEP = pump?.bep_rate_standard_pump_bpd || 0;
  const bepHead = getNominalPressureHeadFt(
    qBEP,
    pump?.hb,
    pump?.h1,
    pump?.h2,
    pump?.h3,
    pump?.h4,
    pump?.h5
  );
  const BEPQgpm = GallonsPerMinute.toThisUnit(qBEP);
  const averageViscosityCentistrokes =
    averageViscosityCP / averageLiquidSpecificGravity;
  // constrain B 1<=B<=40 - change from Ben
  const calculatedB: number =
    26.6 *
    ((Math.pow(averageViscosityCentistrokes, 0.5) * Math.pow(bepHead, 0.0625)) /
      (Math.pow(BEPQgpm, 0.375) * Math.pow(DEFAULT_ESP_PUMP_RPM, 0.25)));
  const constrainedB = Math.max(1, Math.min(calculatedB, 40));
  // cq
  const cq: number = Math.pow(
    2.71,
    -0.165 * Math.pow(Math.log10(constrainedB), 3.15)
  );
  correctionData.recRateCorrection = cq;
  const QwBPD: number = liquidRateSTBD * (DEFAULT_ESP_PUMP_RPM / operatingRPM);
  const Qw: number = GallonsPerMinute.toThisUnit(QwBPD);

  // ch
  const ch: number = 1 - (1 - cq) * Math.pow(Qw / BEPQgpm, 0.75);
  correctionData.recHeadCorrection = ch;
  const Qvis: number = Qw * cq;
  const Hw: number =
    (pump?.hb +
      pump?.h1 * QwBPD +
      pump?.h2 * QwBPD ** 2 +
      pump?.h3 * QwBPD ** 3 +
      pump?.h4 * QwBPD ** 4 +
      pump?.h5 * QwBPD ** 5) *
    (DEFAULT_ESP_PUMP_RPM / operatingRPM) ** 2;
  const BHPw: number =
    (pump?.bhpb +
      pump?.bhp1 * QwBPD +
      pump?.bhp2 * QwBPD ** 2 +
      pump?.bhp3 * QwBPD ** 3 +
      pump?.bhp4 * QwBPD ** 4 +
      pump?.bhp5 * QwBPD ** 5) *
    (DEFAULT_ESP_PUMP_RPM / operatingRPM) ** 3;
  const nw: number =
    (Hw * Qw * averageLiquidSpecificGravity) / (3954.625 * BHPw);
  const cn: number = Math.pow(
    constrainedB,
    -0.0547 * Math.pow(constrainedB, 0.69)
  );

  const hvis = Hw * ch;
  const nvis = nw * cn;
  const Pvis: number =
    (Qvis * hvis * averageLiquidSpecificGravity) / (3954.625 * nvis);

  const chp: number = Pvis / BHPw;
  correctionData.recBHPCorrection = chp;
  return correctionData;
}
